import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContentType } from 'component/Incentive/Badge/__config/TooltipContentType';

import { useQueryEndpoint } from 'hooks/useQueryEndpoint';

import { getFormattedPrice, PriceFormatter } from 'utility/Format/Price';

import { ApiEndpoint } from 'types/Request/endpoint';

import styles from './Discount.module.scss';

export type DiscountType =
    | TooltipContentType.DISCOUNT
    | TooltipContentType.EARLY_BIRD
    | TooltipContentType.LAST_MINUTE
    | TooltipContentType.LAST_MINUTE_HOLIDAYHOME
    | TooltipContentType.EARLY_BIRD_HOLIDAYHOME;

export interface DiscountProps {
    /** discount type */
    type: DiscountType;
}

const Discount = ({ type }: DiscountProps) => {
    const { data, error, isFetching } = useQueryEndpoint(
        ApiEndpoint.CalculationSample,
        {},
        {
            enabled: true,
            useErrorBoundary: false,
        }
    );
    const { t } = useTranslation();

    if (!data || isFetching || error) {
        return null;
    }

    const savingPrice = data[type].price - data[type].discountPrice;
    const savingPercent = Math.round((savingPrice / data[type].price) * 100);

    return (
        <div>
            <div className={styles.Headline}>{data[type].headline}</div>
            <div className={styles.Text}>
                {t(`TRANS-tooltip-discount-text-1-${type}`)}
            </div>
            <div className={styles.Graph}>
                <div
                    className={styles.Bar}
                    style={{ width: `${100 - savingPercent}%` }}
                >
                    <span className={styles.Spike}></span>
                </div>
                <div className={`${styles.Bubble} ${styles.Normal}`}>
                    {t('TRANS-tooltip-discount-info-price')}
                    <div className={styles.Price}>
                        {getFormattedPrice(
                            data[type].price,
                            PriceFormatter.WithFractionDigits
                        )}
                    </div>
                </div>
                <div className={styles.Bubble}>
                    {t('TRANS-tooltip-discount-info-discount-price')}
                    <div className={styles.Price}>
                        {getFormattedPrice(
                            data[type].discountPrice,
                            PriceFormatter.WithFractionDigits
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.Saving}>
                <div className={styles.Price}>
                    {getFormattedPrice(
                        savingPrice,
                        PriceFormatter.WithFractionDigits
                    )}
                </div>
                {t('TRANS-tooltip-discount-info-saving-percent', {
                    percent: savingPercent,
                })}
            </div>
            <div className={styles.Text}>
                {t('TRANS-tooltip-discount-text-2', {
                    additional: data[type].additional,
                    departureCity: data[type].departureCity,
                    destination: data[type].destination,
                    duration: data[type].duration,
                    from: data[type].from,
                    accommodation: data[type].accommodation,
                    mealType: data[type].mealType,
                    roomType: data[type].roomType,
                    region: data[type].region,
                    touroperator: data[type].touroperator,
                    traveller: data[type].traveller,
                    until: data[type].until,
                })}
            </div>
            <div className={styles.Example}>
                <span>
                    <b>{t('TRANS-tooltip-discount-price-text')}</b>
                    <br />
                    {t('TRANS-tooltip-discount-calculated-on', {
                        date: data[type].priceDate,
                    })}
                </span>
                <b>
                    {getFormattedPrice(
                        data[type].price,
                        PriceFormatter.WithFractionDigits
                    )}
                </b>
            </div>
            <div className={styles.Example}>
                <span>
                    <b>{t(`TRANS-tooltip-discount-text-3-${type}`)}</b>
                    <br />
                    {t('TRANS-tooltip-discount-calculated-on', {
                        date: data[type].discountDate,
                    })}
                </span>
                <b>
                    {getFormattedPrice(
                        data[type].discountPrice,
                        PriceFormatter.WithFractionDigits
                    )}
                </b>
            </div>
            <div className={styles.Text}>
                {t('TRANS-tooltip-discount-text-4')}
            </div>
            <span className={styles.Info}>
                {t('TRANS-tooltip-discount-source-domain')}
                {t('TRANS-tooltip-discount-info-source', {
                    date: data[type].discountDate,
                })}
            </span>
        </div>
    );
};

export default Discount;
